/**
 * horstplan-owned metadata that travels alongside `@o2d/rich-text-editor`'s
 * own localStorage draft (`o2d.rte-draft.<uid>`, written and read by the
 * package's `DraftStore`, which knows neither an owner nor a baseline).
 *
 * Review 2026-09-19 (F12/F13): the RTE silent-resume path had no way to
 * tell a stale draft (someone else saved since) or a foreign one (another
 * user in the same browser, e.g. after a session expiry that never cleared
 * localStorage) from the current user's own — unlike the whiteboard, whose
 * draft entry carries `baselineUpdatedAt` in its value and the owner in its
 * KEY (`o2d.whiteboard-draft.<uid>.<userUid>`, `whiteboard-draft-store.helper.ts`).
 * The package cannot be asked to store either, so this side-car key does:
 * `o2d.rte-draft-meta.<uid>` =
 * `{userUid, baselineUpdatedAt}`, written by `knowledge-rte.component.ts` on
 * the first dirty `rich-text-changed` of an edit session.
 *
 * A package draft WITHOUT a meta entry (written before this existed, or in
 * the ≤300 ms window before the first `rich-text-changed` lands) is treated
 * as "owner unknown": shown by the sidebar indicator, never silently
 * resumed, and not baseline-checked (there is nothing to compare). The
 * explicit "Bearbeiten" asks before applying it (H2: e.g. the package's
 * unmount flush re-writes a draft right after a logout's `localStorage.clear()`).
 *
 * KNOWN LIMIT (review 2026-09-19, G4): the package's own key is fixed and
 * not user-scoped, and the package restores whatever it finds on mount. A
 * draft owned by ANOTHER user is therefore discarded when this user opens
 * the document (`discardRteDraft`) — the real owner loses it if two users
 * share a browser without a logout in between. Only a package change could
 * avoid that; the whiteboard store uses a user-scoped key instead.
 *
 * Destructive callers must not treat an UNKNOWN current user (`''`) as
 * foreign: `isRteDraftForeign` fails closed for READERS (nothing is applied
 * or shown for an unknown user), but discarding is left to callers that
 * know who the user is (G3).
 */

interface RteDraftMeta {
    readonly userUid: string;
    readonly baselineUpdatedAt: string;
}

const RTE_DRAFT_KEY_PREFIX = "o2d.rte-draft.";
const RTE_DRAFT_META_KEY_PREFIX = "o2d.rte-draft-meta.";

export function saveRteDraftMeta(uid: string, meta: RteDraftMeta): void {
    try {
        localStorage.setItem(`${RTE_DRAFT_META_KEY_PREFIX}${uid}`, JSON.stringify(meta));
    } catch (error) {
        console.error(`Failed to store the draft metadata for «${uid}».`, error);
    }
}

/** Returns `undefined` on an absent or malformed entry — never throws. */
export function loadRteDraftMeta(uid: string): RteDraftMeta | undefined {
    try {
        const raw = localStorage.getItem(`${RTE_DRAFT_META_KEY_PREFIX}${uid}`);
        if (raw === null) {
            return undefined;
        }
        const parsed = JSON.parse(raw) as Partial<RteDraftMeta>;
        if (typeof parsed.userUid !== 'string' || typeof parsed.baselineUpdatedAt !== 'string') {
            return undefined;
        }
        return parsed as RteDraftMeta;
    } catch {
        return undefined;
    }
}

/**
 * True when a meta entry exists and names a DIFFERENT user than `userUid`.
 * An unknown current user (`''`) fails closed: any owned draft is foreign.
 */
export function isRteDraftForeign(uid: string, userUid: string): boolean {
    const meta = loadRteDraftMeta(uid);
    return meta !== undefined && (userUid === '' || meta.userUid !== userUid);
}

export function clearRteDraftMeta(uid: string): void {
    try {
        localStorage.removeItem(`${RTE_DRAFT_META_KEY_PREFIX}${uid}`);
    } catch {
        // Best-effort, same as the whiteboard store's clear.
    }
}

/**
 * Removes the package's own draft key AND the side-car meta. horstplan
 * already peeks at the package's key format (`hasKnowledgeDraft`); removing
 * it here is the only way to discard a stale draft the package would
 * otherwise restore on the next edit-mode mount.
 */
export function discardRteDraft(uid: string): void {
    try {
        localStorage.removeItem(`${RTE_DRAFT_KEY_PREFIX}${uid}`);
    } catch {
        // Best-effort.
    }
    clearRteDraftMeta(uid);
}
